import type { PaymentMethod, PeriodKey, TransactionType } from '@/types'

export const TYPE_LABELS: Record<TransactionType, string> = {
    income: 'Pemasukan',
    expense: 'Pengeluaran'
}

export const PAYMENT_METHODS: { value: PaymentMethod, label: string }[] = [
    { value: 'cash', label: 'Tunai' },
    { value: 'ewallet', label: 'Dompet Digital' },
    { value: 'qris', label: 'QRIS' },
    { value: 'debit', label: 'Kartu Debit' },
    { value: 'credit', label: 'Kartu Kredit' },
    { value: 'transfer', label: 'Transfer Bank' }
]

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    cash: 'Tunai',
    ewallet: 'Dompet Digital',
    qris: 'QRIS',
    debit: 'Kartu Debit',
    credit: 'Kartu Kredit',
    transfer: 'Transfer Bank'
}

export const isPaymentMethod = (value: unknown): value is PaymentMethod => {
    return typeof value === 'string' && value in PAYMENT_METHOD_LABELS
}

export const isTransactionType = (value: unknown): value is TransactionType => {
    return value === 'income' || value === 'expense'
}

export const CATEGORY_ICON_KEYS = [
    'utensils',
    'coffee',
    'bus',
    'car',
    'fuel',
    'plane',
    'graduation-cap',
    'book-open',
    'shopping-bag',
    'shirt',
    'receipt',
    'zap',
    'wifi',
    'smartphone',
    'house',
    'heart-pulse',
    'clapperboard',
    'gamepad',
    'music',
    'dumbbell',
    'gift',
    'paw-print',
    'baby',
    'briefcase',
    'wallet',
    'laptop',
    'trending-up',
    'piggy-bank',
    'hand-coins',
    'shapes'
] as const

export type CategoryIconKey = (typeof CATEGORY_ICON_KEYS)[number]

export const isCategoryIcon = (value: unknown): value is CategoryIconKey => {
    return typeof value === 'string' && (CATEGORY_ICON_KEYS as readonly string[]).includes(value)
}

export const CATEGORY_COLORS: { value: string, label: string }[] = [
    { value: '#F28E2B', label: 'Oranye' },
    { value: '#4E79A7', label: 'Biru' },
    { value: '#B07AA1', label: 'Ungu' },
    { value: '#FF9DA7', label: 'Merah muda' },
    { value: '#9C755F', label: 'Cokelat' },
    { value: '#EDC948', label: 'Kuning' },
    { value: '#76B7B2', label: 'Hijau toska' },
    { value: '#59A14F', label: 'Hijau' },
    { value: '#E15759', label: 'Merah' },
    { value: '#2F4B7C', label: 'Biru tua' },
    { value: '#A0CBE8', label: 'Biru muda' },
    { value: '#BAB0AC', label: 'Abu-abu' }
]

export const FALLBACK_CATEGORY_COLOR = '#BAB0AC'

export const DEFAULT_CATEGORIES: { name: string, type: TransactionType, color: string, icon: CategoryIconKey }[] = [
    { name: 'Makanan & Minuman', type: 'expense', color: '#F28E2B', icon: 'utensils' },
    { name: 'Transportasi', type: 'expense', color: '#4E79A7', icon: 'bus' },
    { name: 'Pendidikan', type: 'expense', color: '#B07AA1', icon: 'graduation-cap' },
    { name: 'Belanja', type: 'expense', color: '#FF9DA7', icon: 'shopping-bag' },
    { name: 'Tagihan', type: 'expense', color: '#9C755F', icon: 'receipt' },
    { name: 'Hiburan', type: 'expense', color: '#EDC948', icon: 'clapperboard' },
    { name: 'Lainnya', type: 'expense', color: '#BAB0AC', icon: 'shapes' },
    { name: 'Gaji', type: 'income', color: '#4E79A7', icon: 'briefcase' },
    { name: 'Uang Saku', type: 'income', color: '#F28E2B', icon: 'wallet' },
    { name: 'Freelance', type: 'income', color: '#B07AA1', icon: 'laptop' },
    { name: 'Bonus', type: 'income', color: '#EDC948', icon: 'gift' },
    { name: 'Hasil Investasi', type: 'income', color: '#9C755F', icon: 'trending-up' },
    { name: 'Lainnya', type: 'income', color: '#BAB0AC', icon: 'shapes' }
]

export const PERIOD_OPTIONS: { value: PeriodKey, label: string }[] = [
    { value: 'bulan-ini', label: 'Bulan ini' },
    { value: 'bulan-lalu', label: 'Bulan lalu' },
    { value: '30-hari', label: '30 hari terakhir' },
    { value: 'tahun-ini', label: 'Tahun ini' },
    { value: 'semua', label: 'Semua waktu' }
]

export const isPeriodKey = (value: unknown): value is PeriodKey => {
    return PERIOD_OPTIONS.some(option => option.value === value)
}

export const LIMITS = {
    amountMax: 999_999_999_999,
    titleMax: 100,
    notesMax: 500,
    categoryNameMax: 40,
    categoriesPerType: 30,
    nameMin: 2,
    nameMax: 60,
    emailMax: 254,
    passwordMin: 8,
    passwordMax: 72,
    openingBalanceMax: 999_999_999_999,
    pageSize: 20
}
